import { styled } from '@mui/material';

import { useMemo } from 'react';

import { Typography } from '@app-velocity/ui';

import { ThumbsDownIconBig, ThumbsUpIconBig } from '@app-velocity/icons';
import { Loader } from '@app-velocity/animations';
import { I18nResources, useI18n } from '@app-velocity/i18n';

import { MultipleProgress } from '@app-velocity/modules/scenarios';

import { useSimulationWidgetContext } from '../../../../hooks/useSimulationWidgetContext';

const IconContainer = styled('div')<{ type: 'success' | 'error' }>(
  ({ type, theme }) => ({
    padding: theme.spacing(1),
    height: '100%',
    svg: {
      fill:
        type === 'success'
          ? 'var(--success-success-icon-primary-color)'
          : 'var(--error-error-icon-primary-color)',
      height: '100%',
      width: '100%',
    },
  })
);

const StyledContent = styled('div')({
  flex: 1,
  overflow: 'hidden',
});

const StyledStatusText = styled(Typography)({
  fontSize: 18,
  fontWeight: 700,
  textAlign: 'center',
});

const ContentContainer = styled('div')<{
  status?: null | 'IN_PROGRESS' | 'CANCELED' | 'COMPLETED' | 'RESTARTED';
}>(({ theme, status }) => ({
  minHeight: status ? theme.spacing(45) : undefined,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  gap: theme.spacing(2),
  width: '100%',
}));

export const SimulationStatus = () => {
  const { t } = useI18n();
  const { parsedStatus, statistics } = useSimulationWidgetContext();

  const completedSimulations = statistics?.simulatedReviews.succeded || 0;
  const failedSimulations = statistics?.simulatedReviews.failed || 0;

  const content = useMemo(() => {
    if (!parsedStatus) return null;

    if (parsedStatus === 'IN_PROGRESS') return <Loader />;

    if (completedSimulations + failedSimulations > 1) {
      return (
        <MultipleProgress
          successes={completedSimulations}
          fails={failedSimulations}
        />
      );
    }

    if (completedSimulations === 1)
      return (
        <IconContainer type="success">
          <ThumbsUpIconBig />
        </IconContainer>
      );

    if (failedSimulations === 1)
      return (
        <IconContainer type="error">
          <ThumbsDownIconBig />
        </IconContainer>
      );
  }, [parsedStatus, completedSimulations, failedSimulations]);

  const text: string = useMemo(() => {
    if (!parsedStatus) {
      if (
        !statistics?.simulatedReviews.abortedByUser &&
        !statistics?.simulatedReviews.abortedByErrorOrTimeout
      ) {
        return t('prototypes:no_simulations');
      }

      return t('prototypes:no_successful_simulations');
    }

    if (parsedStatus === 'IN_PROGRESS') return t('prototypes:loading');

    if (completedSimulations + failedSimulations === 1) {
      if (completedSimulations === 1) return t('prototypes:pass');

      return t('prototypes:fail');
    }

    return t('prototypes:passed_n_of_m', {
      passed: completedSimulations,
      all: completedSimulations + failedSimulations,
    });
  }, [parsedStatus, completedSimulations, failedSimulations, t]);

  const additionalText: I18nResources | null = useMemo(() => {
    switch (parsedStatus) {
      case 'IN_PROGRESS':
        return 'prototypes:running_in_background';
      default:
        return null;
    }
  }, [parsedStatus]);

  return (
    <ContentContainer status={parsedStatus}>
      <StyledContent>{content}</StyledContent>
      <StyledStatusText>{text}</StyledStatusText>
      {additionalText ? <Typography>{t(additionalText)}</Typography> : null}
    </ContentContainer>
  );
};
